import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { useRouteMatch } from 'react-router-dom'
import { gql, useMutation, useQuery } from '@apollo/client'
import { yupResolver } from '@hookform/resolvers'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import {
  Box,
  Button,
  LinearProgress,
  TextField,
  Typography,
} from '@material-ui/core'
import ComputerOutlinedIcon from 'components/material-symbol/icons/Computer'
import OpenInNewOutlinedIcon from 'components/material-symbol/icons/OpenInNew'
import InfoOutlinedIcon from 'components/material-symbol/icons/Info'
import CheckIcon from 'components/material-symbol/icons/Check'
import { useAuth } from 'lib/auth'
import { usePortal } from 'lib/portal'
import { useSnackbarSuccess, useSnackbarError } from 'lib/snackbar'
import { useSnackbar } from 'notistack'
import { PageHeader } from 'components/page-header'
import { FormActionBar } from 'components/form-util'
import useStyles from './styles'
import useSettingsStyles from '../settings.styles'
import { SettingsChoiceRow, SettingsSection } from '../settings-components'
import { isExternalBookingUrl } from 'lib/coach-profile/marketplace-listing'

export const GET_BOOKING_URL = gql`
  query CoachBookingUrl {
    currentUser {
      attributes {
        ... on Coach {
          bookingUrl
          preferences {
            bookNowEnabled
          }
        }
      }
    }
  }
`

const UPDATE_COACH_EXTERNAL_BOOKING_URL = gql`
  mutation UpdateCoachBookingUrl($externalBookingUrl: URL) {
    updateCoachExternalBookingUrl(externalBookingUrl: $externalBookingUrl) {
      success
      message
      coach {
        bookingUrl
        id
      }
    }
  }
`

const validationSchema = yup.object().shape({
  externalBookingUrl: yup
    .string()
    .required('Please enter a URL.')
    .url(
      'Please enter a valid URL. URL must start with "http://" or "https://"',
    )
    .matches(
      /^(?!.*.pga.com)/i,
      'Please enter a URL that is not associated with PGA.',
    ),
})

const BookingUrlPage = () => {
  const { path } = useRouteMatch()
  const { user } = useAuth()
  const [target] = usePortal()
  const classes = useStyles()
  const settingsStyles = useSettingsStyles()

  const { enqueueSnackbar } = useSnackbar()
  const snackbarSuccess = useSnackbarSuccess(enqueueSnackbar)
  const snackbarError = useSnackbarError(enqueueSnackbar)

  const { data, loading, refetch } = useQuery(GET_BOOKING_URL, {
    fetchPolicy: 'cache-and-network',
  })

  const bookingUrl =
    data?.currentUser?.attributes?.bookingUrl ?? user?.coach?.bookingUrl ?? ''

  const bookNowEnabled = !!data?.currentUser?.attributes?.preferences
    ?.bookNowEnabled

  const isPgaUrl = !isExternalBookingUrl(bookingUrl)
  const [externalSelected, setExternalSelected] = useState(false)
  const syncedBookingUrlRef = useRef(null)

  const { control, handleSubmit, errors, reset, watch } = useForm({
    mode: 'onChange',
    defaultValues: {
      externalBookingUrl: '',
    },
    resolver: yupResolver(validationSchema),
  })

  const externalBookingUrl = watch('externalBookingUrl') || ''

  const syncFromServer = useCallback(
    url => {
      const usingExternal = isExternalBookingUrl(url)

      setExternalSelected(usingExternal)
      reset({
        externalBookingUrl: usingExternal ? url : '',
      })
    },
    [reset],
  )

  useEffect(() => {
    if (loading || !data?.currentUser) return

    const serverUrl =
      data.currentUser.attributes?.bookingUrl ?? user?.coach?.bookingUrl ?? ''

    if (syncedBookingUrlRef.current === serverUrl) return

    syncedBookingUrlRef.current = serverUrl
    syncFromServer(serverUrl)
  }, [loading, data, user, syncFromServer])

  const [setBookingUrl, { loading: saving }] = useMutation(
    UPDATE_COACH_EXTERNAL_BOOKING_URL,
    {
      onCompleted: async data => {
        const urlChanged = data?.updateCoachExternalBookingUrl?.success
        if (!urlChanged) {
          snackbarError('"Book Now" preferences were not set.')
        } else {
          snackbarSuccess(data.updateCoachExternalBookingUrl?.message)
        }
        const { data: refetched } = await refetch()
        const nextBookingUrl =
          refetched?.currentUser?.attributes?.bookingUrl ?? ''
        syncedBookingUrlRef.current = nextBookingUrl
        syncFromServer(nextBookingUrl)
      },
      onError: error => {
        window.rg4js &&
          window.rg4js('send', {
            error: error,
            tags: ['set-booking-url'],
          })
        snackbarError('"Book Now" preferences were not set.')
      },
    },
  )

  const crumbs = [
    {
      label: 'Settings',
      to: path
        .split('/')
        .slice(0, 3)
        .join('/'),
    },
  ]

  const isDirty = useMemo(() => {
    if (loading) return false
    if (!externalSelected) return !isPgaUrl
    return isPgaUrl || externalBookingUrl !== (bookingUrl || '')
  }, [loading, externalSelected, isPgaUrl, externalBookingUrl, bookingUrl])

  const canSave = useMemo(() => {
    if (!isDirty) return false
    if (!externalSelected) return true
    return !errors.externalBookingUrl && !!externalBookingUrl
  }, [isDirty, externalSelected, errors.externalBookingUrl, externalBookingUrl])

  const onSubmit = async formValues => {
    if (!externalSelected) {
      await setBookingUrl({
        variables: { externalBookingUrl: null },
      })
      return
    }

    await setBookingUrl({
      variables: {
        externalBookingUrl: formValues.externalBookingUrl,
      },
    })
  }

  const submitBookingPreferences = event => {
    event.preventDefault()

    if (!externalSelected) {
      onSubmit({})
      return
    }

    handleSubmit(onSubmit)(event)
  }

  if (loading) return <LinearProgress color="primary" />

  return (
    <>
      <PageHeader
        title="Book Now preferences"
        crumbs={crumbs}
        actions={target}
      />
      {isDirty && (
        <FormActionBar variant="toolbar" includeCancel disableCancel={saving}>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            form="booking-url-form"
            startIcon={<CheckIcon fontSize="small" />}
            disabled={!canSave || saving}
          >
            Save
          </Button>
        </FormActionBar>
      )}
      <Box
        className={clsx(
          settingsStyles.contentContainer,
          isDirty && settingsStyles.contentContainerWithStickySave,
        )}
      >
        {!bookNowEnabled && (
          <Box className={classes.infoBanner}>
            <Box className={classes.infoBannerCopy}>
              <InfoOutlinedIcon
                className={classes.infoBannerIcon}
                fontSize="small"
                opticalSize={20}
              />
              <Typography component="span" className={classes.infoBannerText}>
                To allow students to book with you and register for your events,
                make sure your &quot;Book Now&quot; toggle is on.
              </Typography>
            </Box>
          </Box>
        )}

        <form id="booking-url-form" onSubmit={submitBookingPreferences}>
          <SettingsSection
            eyebrow="Book Now preferences"
            classes={settingsStyles}
          >
            <SettingsChoiceRow
              icon={<ComputerOutlinedIcon />}
              title="PGA Coach"
              badge="Default"
              description="Use PGA Coach to promote, collect, and manage bookings and event registrations from the web or mobile apps."
              checked={!externalSelected}
              onSelect={() => setExternalSelected(false)}
              classes={settingsStyles}
            />
            <SettingsChoiceRow
              icon={<OpenInNewOutlinedIcon />}
              title="External booking link"
              description="Send students to the booking page you already use, such as Calendly, your own website, GolfTEC, or an Outlook calendar. PGA Coach cannot promote your programs or accept sign-ups."
              checked={externalSelected}
              onSelect={() => setExternalSelected(true)}
              classes={settingsStyles}
            />
            {externalSelected ? (
              <Box className={classes.urlField}>
                <Controller
                  name="externalBookingUrl"
                  control={control}
                  render={({ onChange, onBlur, value, ref }) => (
                    <TextField
                      inputRef={ref}
                      value={value || ''}
                      onChange={onChange}
                      onBlur={onBlur}
                      variant="outlined"
                      fullWidth
                      error={!!errors.externalBookingUrl}
                      helperText={errors.externalBookingUrl?.message}
                      label="URL"
                      placeholder="https://www.bookinglink.com"
                    />
                  )}
                />
              </Box>
            ) : null}
          </SettingsSection>
        </form>
      </Box>
    </>
  )
}

export default BookingUrlPage
